import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

interface TaskSummary {
  id: string
  user_request: string
  status: string
  created_at: string
  completed_at?: string
  duration_minutes?: number
  total_cost: number
  agent_count: number
  agent_instances: number
  message_count: number
  agent_names?: string
}

function Dashboard() {
  const [tasks, setTasks] = useState<TaskSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchTasks()
  }, [])

  const fetchTasks = async () => {
    try {
      const response = await fetch('/api/tasks?user_id=1')
      if (!response.ok) throw new Error('Failed to fetch tasks')
      const data = await response.json()
      setTasks(data)
    } catch (err) {
      setError('Failed to load tasks')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending_plan': 'bg-yellow-100 text-yellow-800',
      'awaiting_approval': 'bg-blue-100 text-blue-800',
      'running': 'bg-green-100 text-green-800',
      'completed': 'bg-gray-100 text-gray-800',
      'failed': 'bg-red-100 text-red-800',
      'rejected': 'bg-gray-100 text-gray-600'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'pending_plan': 'Pendente',
      'awaiting_approval': 'Aguardando Aprovação',
      'running': 'Executando',
      'completed': 'Concluído',
      'failed': 'Falhou',
      'rejected': 'Rejeitado'
    }
    return labels[status] || status
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Carregando...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Gerencie suas tarefas multi-agente</p>
      </div>

      {tasks.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">Nenhuma tarefa ainda.</p>
          <p className="text-gray-400">Use o bot do Telegram para criar tarefas.</p>
        </div>
      ) : (
        <div className="grid gap-6">
          {tasks.map((task) => (
            <Link
              key={task.id}
              to={`/task/${task.id}`}
              className="card hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {task.user_request.substring(0, 100)}
                    {task.user_request.length > 100 && '...'}
                  </h3>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                      {getStatusLabel(task.status)}
                    </span>
                    <span>💰 ${task.total_cost.toFixed(4)}</span>
                    <span>🤖 {task.agent_count} agentes</span>
                    <span>💬 {task.message_count} mensagens</span>
                  </div>
                  
                  {task.agent_names && (
                    <p className="text-sm text-gray-400 mt-2">
                      Agentes: {task.agent_names}
                    </p>
                  )}
                </div>
                
                <div className="text-right text-sm text-gray-400">
                  <p>{new Date(task.created_at).toLocaleDateString()}</p>
                  {task.duration_minutes && (
                    <p>{Math.round(task.duration_minutes)} min</p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default Dashboard
